import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomModal from '../../../util_components/custom_modal';
import { image_error_replace } from '../../../../util_functions';
import { post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import * as actions from '../../../../redux/action_creators';
import DateRangePicker from '../../../util_components/date_range_picker';
import RequestSession from '../../students/request_session';

/**
 * Request Session Dashboard (care team) — Figma "Request Session Dashboard".
 *
 * Lists session requests created by the care team with their broadcast status, and lets care
 * resend / cancel a request and inspect which matched teachers have viewed/declined it.
 *
 * Wired to goserver (ops token):
 *   POST /v2/session_request/care/list      (A3) — rows
 *   POST /v2/session_request/care/activity  (A4) — teacher breakdown (info icon)
 *   POST /v2/session_request/care/resend    (A5) — re-broadcast
 *   POST /v2/session_request/care/cancel    (A6) — cancel
 */

const STATUS_STYLE = {
  pending: { bg: 'var(--badge-pending-bg)', color: 'var(--badge-pending-text)', label: 'Pending' },
  accepted: {
    bg: 'var(--badge-accepted-bg)',
    color: 'var(--badge-accepted-text)',
    label: 'Accepted'
  },
  cancelled: {
    bg: 'var(--badge-cancelled-bg)',
    color: 'var(--badge-cancelled-text)',
    label: 'Cancelled'
  },
  expired: {
    bg: 'var(--brand-primary-subtle-bg)',
    color: 'var(--brand-primary)',
    label: 'Expired'
  },
  failed: {
    bg: 'var(--badge-cancelled-bg)',
    color: 'var(--badge-cancelled-text)',
    label: 'Failed'
  }
};

const COLUMNS = [
  'Requested By',
  'Request Date & Time',
  'Student Profile',
  'Class Type',
  'Student Date & Time',
  'Teacher Date & Time (IST)',
  'Accepted By',
  'Status',
  'Request Info',
  'Action'
];

const th_style = {
  padding: '14px 16px',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textAlign: 'left',
  whiteSpace: 'nowrap'
};

const td_style = {
  padding: '16px',
  fontSize: 13,
  color: 'var(--text-primary)',
  borderTop: '1px solid var(--border-default)',
  verticalAlign: 'middle',
  whiteSpace: 'pre-line'
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '7px 10px',
        borderRadius: 5,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        background: s.bg,
        color: s.color
      }}
    >
      {s.label}
    </span>
  );
}

const format_request_time = (ms) => {
  if (!ms) return '-';
  const m = moment(ms).tz('Asia/Kolkata');
  if (!m.isValid()) return '-';
  return `${m.format('ddd, MMM D, YY')}\n${m.format('h:mm A')} IST`;
};

// Default filter window — last 30 days of request (created) dates, matching the Figma pill.
const default_date_range = () => ({
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD')
});

function RequestSessionDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  // Teacher-marked goals (config_values.goals_for_1on1_fitness) so the reused Request-a-Session
  // popup shows the full goal list when care edits a resend; falls back to its static list if absent.
  const goalOptions = useSelector((s) => s.home.config_values?.goals_for_1on1_fitness);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(default_date_range);

  const [infoRow, setInfoRow] = useState(null);
  const [activity, setActivity] = useState(null); // { not_viewed, viewed, declined }
  const [activityLoading, setActivityLoading] = useState(false);

  const [resendRow, setResendRow] = useState(null);
  const [actionUuid, setActionUuid] = useState(null); // uuid whose resend/cancel is in flight

  const notify = useCallback(
    (type, message) => dispatch(actions.set_notification_variable(true, type, message)),
    [dispatch]
  );

  const err_message = (e, fallback) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (err) {
      return fallback;
    }
  };

  const load_requests = useCallback(() => {
    setLoading(true);
    // from_date/to_date are epoch_ms filtering on request created_date (0 => unbounded).
    const from_date = dateRange.startDate
      ? moment(dateRange.startDate).startOf('day').valueOf()
      : 0;
    const to_date = dateRange.endDate ? moment(dateRange.endDate).endOf('day').valueOf() : 0;
    const url = getApiUrl('/v2/session_request/care/list', false, true);
    postApi(url, { status: 'all', from_date, to_date, page: 0, item_limit: 50 }, true)
      .then((resp) => {
        setRows(resp.data?.data?.session_requests || []);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        notify('error', err_message(e, 'Could not load session requests.'));
      });
  }, [notify, dateRange]);

  useEffect(() => {
    load_requests();
  }, [load_requests]);

  const open_info = (row) => {
    setInfoRow(row);
    setActivity(null);
    setActivityLoading(true);
    const url = getApiUrl('/v2/session_request/care/activity', false, true);
    postApi(url, { session_request_uuid: row.uuid }, true)
      .then((resp) => {
        const data = resp.data?.data || {};
        const viewed_or_declined = data.viewed_or_declined || [];
        setActivity({
          not_viewed: data.not_viewed || [],
          viewed: viewed_or_declined.filter((t) => t.status === 'viewed'),
          declined: viewed_or_declined.filter((t) => t.status === 'declined')
        });
        setActivityLoading(false);
      })
      .catch((e) => {
        setActivityLoading(false);
        notify('error', err_message(e, 'Could not load request activity.'));
      });
  };

  const on_cancel = (row) => {
    setActionUuid(row.uuid);
    const url = getApiUrl('/v2/session_request/care/cancel', false, true);
    postApi(url, { session_request_uuid: row.uuid }, true)
      .then(() => {
        setActionUuid(null);
        notify('success', 'Session request cancelled.');
        load_requests();
      })
      .catch((e) => {
        setActionUuid(null);
        notify('error', err_message(e, 'Could not cancel the request.'));
      });
  };

  // Prefill values for the reused Request-a-Session popup (Resend opens it at the confirm step;
  // Edit reveals the editable form). sessionDate is the student's wall-clock time rebuilt as a
  // local Date so the popup re-interprets it in the student's zone the same way it was created.
  const resend_initial = (row) => {
    const zone = row.student_timezone || moment.tz.guess();
    const wall = row.start_time ? moment.tz(row.start_time, zone).format('YYYY-MM-DDTHH:mm') : null;
    return {
      sessionDate: wall ? moment(wall).toDate() : null,
      duration: row.duration_minutes || undefined,
      coachGender: row.coach_gender || 'NO_PREFERENCE',
      languages: row.language
        ? row.language
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      goals: row.goals || [],
      specificNeeds: row.kind_of_session || ''
    };
  };

  const render_status_row = (message, muted) => (
    <tr>
      <td
        style={{ ...td_style, textAlign: 'center', color: muted ? 'var(--text-muted)' : undefined }}
        colSpan={COLUMNS.length}
      >
        {message}
      </td>
    </tr>
  );

  const render_action = (row) => {
    if (row.status === 'accepted' || row.status === 'expired' || row.status === 'failed') {
      return <span style={{ color: 'var(--text-muted)' }}>-</span>;
    }
    const busy = actionUuid === row.uuid;
    const cancelDisabled = row.status !== 'pending' || busy;
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          style={{ ...resendBtn, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
          disabled={busy}
          onClick={() => setResendRow(row)}
        >
          Resend
        </button>
        <button
          type="button"
          disabled={cancelDisabled}
          style={{
            ...cancelBtn,
            opacity: cancelDisabled ? 0.4 : 1,
            cursor: cancelDisabled ? 'not-allowed' : 'pointer'
          }}
          onClick={() => !cancelDisabled && on_cancel(row)}
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <CommonHeader show_back title="Request Session Dashboard">
      <Helmet>
        <title>Request Session Dashboard</title>
      </Helmet>

      <div style={{ padding: '0 8px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0 16px' }}>
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onApply={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
            align="right"
            compact
          />
        </div>

        <div
          style={{
            overflowX: 'auto',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 12
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000, margin: 0 }}>
            <thead>
              <tr style={{ background: 'var(--surface-sunken)' }}>
                {COLUMNS.map((c) => (
                  <th key={c} style={th_style}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && render_status_row('Loading…', false)}
              {!loading && rows.length === 0 && render_status_row('No session requests yet.', true)}
              {!loading &&
                rows.map((row) => (
                  <tr key={row.uuid}>
                    <td style={td_style}>{row.requested_by_name || '-'}</td>
                    <td style={{ ...td_style, color: 'var(--text-muted)' }}>
                      {format_request_time(row.created_at)}
                    </td>
                    <td style={td_style}>
                      {row.student_uuid ? (
                        <a
                          href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
                          onClick={(e) => {
                            e.preventDefault();
                            history.push(
                              `/studentprofile?id=${encodeURIComponent(row.student_uuid)}`
                            );
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: 'var(--brand-primary)',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <img
                            src={
                              row.student_profile_photo ||
                              'https://images.myyogateacher.com/user_profile/default-user.png'
                            }
                            onError={image_error_replace}
                            alt={row.student_name}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                          {row.student_name || '-'}
                        </a>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <img
                            src={
                              row.student_profile_photo ||
                              'https://images.myyogateacher.com/user_profile/default-user.png'
                            }
                            onError={image_error_replace}
                            alt={row.student_name}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                          {row.student_name || '-'}
                        </div>
                      )}
                    </td>
                    <td style={td_style}>{row.class_type || '1-on-1'}</td>
                    <td style={td_style}>{row.student_date_time_str || '-'}</td>
                    <td style={td_style}>{row.teacher_date_time_str || '-'}</td>
                    <td style={{ ...td_style, color: 'var(--text-muted)' }}>
                      {row.accepted_by_name || '-'}
                    </td>
                    <td style={td_style}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td style={{ ...td_style, textAlign: 'center' }}>
                      <button
                        type="button"
                        aria-label="Request info"
                        onClick={() => open_info(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: 0,
                          lineHeight: 0
                        }}
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                    <td style={td_style}>{render_action(row)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomModal
        show={!!resendRow}
        close={() => setResendRow(null)}
        inner_style={{ width: 688, maxWidth: '95vw', padding: '20px 40px 32px', borderRadius: 10 }}
      >
        {resendRow ? (
          <RequestSession
            details={{ iana_timezone: resendRow.student_timezone }}
            student_uuid={resendRow.student_uuid}
            goal_options={goalOptions}
            initialValues={resend_initial(resendRow)}
            initialStep="confirm"
            close={() => {
              setResendRow(null);
              load_requests();
            }}
            set_notification_variable={(show, type, message) =>
              dispatch(actions.set_notification_variable(show, type, message))
            }
          />
        ) : null}
      </CustomModal>

      <CustomModal
        show={!!infoRow}
        close={() => setInfoRow(null)}
        inner_style={{ width: 562, maxWidth: '92vw', padding: '20px 28px 28px', borderRadius: 14 }}
      >
        {infoRow ? (
          <div>
            <p
              style={{
                margin: '0 0 22px',
                fontSize: 22,
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}
            >
              Session request activity
            </p>
            {activityLoading || !activity ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p>
            ) : (
              <>
                <TeacherGroup title="Not viewed request" teachers={activity.not_viewed} />
                <TeacherGroup
                  title="Viewed / Declined request"
                  teachers={[...activity.viewed, ...activity.declined]}
                />
              </>
            )}
          </div>
        ) : null}
      </CustomModal>
    </CommonHeader>
  );
}

const DEFAULT_AVATAR = 'https://images.myyogateacher.com/user_profile/default-user.png';

function TeacherGroup({ title, teachers }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)'
        }}
      >
        {title}
      </p>
      <div
        style={{
          background: 'var(--surface-sunken)',
          borderRadius: 12,
          padding: teachers.length ? '6px 16px' : '16px'
        }}
      >
        {teachers.length ? (
          teachers.map((t) => (
            <div
              key={t.teacher_uuid}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0' }}
            >
              {/* Rendered as a background-image span (not <img>) to dodge the global
                `.custom-modal-inner-container img` !important override that would force width:auto. */}
              <span
                aria-label={t.name}
                style={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: 'var(--surface-sunken)',
                  backgroundImage: `url(${t.photo || DEFAULT_AVATAR})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <span style={{ fontSize: 15, color: 'var(--text-primary)' }}>{t.name}</span>
            </div>
          ))
        ) : (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>—</span>
        )}
      </div>
    </div>
  );
}

const resendBtn = {
  padding: '10px 15px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--action-dark-bg)',
  color: '#ffffff',
  fontSize: 14.5,
  fontWeight: 550,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(79, 42, 118, 0.2)'
};

const cancelBtn = {
  padding: '10px 15px',
  borderRadius: 14,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-card)',
  color: 'var(--input-text)',
  fontSize: 14.5,
  fontWeight: 550,
  boxShadow: '0px 2px 4px 0px rgba(188, 188, 188, 0.2)'
};

export default RequestSessionDashboard;
